import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { User, LogOut, LayoutDashboard, ShieldCheck, GraduationCap } from 'lucide-react';

const Navbar = () => {
  const { user, isAuthenticated, isAdmin, logout, openAuthModal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    
    // If already on homepage, reload; otherwise navigate to homepage
    if (location.pathname === '/') {
      window.location.reload();
    } else {
      navigate('/');
    }
  };

  return (
    <nav className="border-b border-dark-border bg-dark-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - Left */}
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img 
                src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4" 
                alt="Emergent Showcase"
                className="w-8 h-8 rounded-full"
              />
              <span className="text-lg font-bold text-white">Emergent Showcase</span>
            </Link>

            {/* Actions - Right */}
            <div className="flex items-center gap-4">
              {/* Learn Vibe Coding Button */}
              <a
                href="https://vibes.emergent.sh/blogs"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-brand-yellow text-dark-bg rounded-lg hover:bg-brand-yellow/90 transition-all font-bold text-sm shadow-lg hover:shadow-xl"
                data-testid="vibe-coding-button"
              >
                <GraduationCap className="w-4 h-4" />
                <span className="hidden sm:inline">Learn Vibe Coding</span>
                <span className="sm:hidden">Vibes</span>
              </a>

              {isAuthenticated ? (
                <>
                  {/* User Menu */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                      data-testid="user-menu-button"
                    >
                      <User className="w-5 h-5" />
                      <span className="hidden sm:inline text-sm font-medium">Profile</span>
                    </button>

                    {showUserMenu && (
                      <div className="absolute top-full right-0 mt-2 w-56 bg-dark-card rounded-lg shadow-xl border border-dark-border py-2 z-50">
                        <div className="px-4 py-2 border-b border-dark-border">
                          <p className="text-sm font-medium text-white">{user?.name}</p>
                          <p className="text-xs text-gray-400">{user?.email}</p>
                        </div>
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-dark-bg"
                          onClick={() => setShowUserMenu(false)}
                          data-testid="dashboard-link"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          My Apps
                        </Link>
                        {isAdmin && (
                          <Link
                            to="/admin"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-dark-bg"
                            onClick={() => setShowUserMenu(false)}
                            data-testid="admin-panel-link"
                          >
                            <ShieldCheck className="w-4 h-4" />
                            Admin Panel
                          </Link>
                        )}
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-dark-bg"
                          data-testid="logout-button"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => openAuthModal()}
                    className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors font-medium text-sm"
                    data-testid="login-button"
                  >
                    <User className="w-5 h-5" />
                    <span className="hidden sm:inline">Profile</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
  );
};

export default Navbar;
